import { consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Badge, Button, Field, Icon, Select, Slider } from "@sagot/ui";
import { useState } from "react";
import { Controller, useFormContext, useWatch, type FieldPath } from "react-hook-form";
import { useWorkspaceContext } from "../../../session/workspace-context";
import { useBillingOverview } from "../../deploy-knowledge/shared/use-shared-queries";
import { useBuildFormatters } from "../shared/format";
import { CHUNK_SIZES, LIMITS, type SettingsValues } from "./settings-model";
import { SettingsCard, SwitchRow } from "./settings-ui";
import { useLatestEval } from "./use-agent-settings";

const EVALUATIONS_FEATURE = "evaluations";
const PERCENT = 100;
const OVERLAP_STEP = 0.05;
const K_STEP = 5;
const SCORE_STEP = 0.05;
const HYBRID_STEP = 5;

type NumberPath = Extract<FieldPath<SettingsValues>, `retrieval.${"chunk_overlap_ratio" | "k_fused" | "k_dense" | "k_lexical" | "final_top_n" | "hybrid" | "rerank_min_score" | "max_chunks_per_source"}`>;

interface RangeProps {
  name: NumberPath;
  label: string;
  min: number;
  max: number;
  step: number;
  disabled: boolean;
  hint?: string;
  format?: (v: number) => string;
  startLabel?: string;
  endLabel?: string;
}

function Range({ name, label, min, max, step, disabled, hint, format, startLabel, endLabel }: RangeProps) {
  const { control } = useFormContext<SettingsValues>();
  return (
    <Controller control={control} name={name} render={({ field }) => (
      <Slider label={label} value={field.value} onValueChange={field.onChange} min={min} max={max} step={step} hint={hint} formatValue={format} startLabel={startLabel} endLabel={endLabel} disabled={disabled} />
    )} />
  );
}

function LastEvaluation({ agentId }: { agentId: string }) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const fmt = useBuildFormatters();
  // Evaluations are a Pro feature: with a known plan that lacks it the call would only answer 402.
  const billing = useBillingOverview();
  const entitled = billing.isError || (billing.isSuccess && billing.data.features.includes(EVALUATIONS_FEATURE));
  const sets = useLatestEval(agentId, entitled);
  const latest = [...(sets.data?.items ?? [])].filter((s) => s.last_run_at).sort((a, b) => (b.last_run_at ?? "").localeCompare(a.last_run_at ?? ""))[0];
  if (!entitled || sets.isError) return null;
  return (
    <div className="flex flex-col items-start gap-3 rounded-lg border border-line bg-surface p-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="text-sm">
        <span className="font-semibold">{t("build.agentSettings.retrieval.lastEval")}</span>{" "}
        <span className="text-fg-muted">
          {latest
            ? t("build.agentSettings.retrieval.lastEvalValue", { date: fmt.date(latest.last_run_at), name: latest.name, pass: latest.last_pass ?? 0, total: latest.last_total ?? latest.case_count })
            : t("build.agentSettings.retrieval.lastEvalNone")}
        </span>
      </div>
      <Button asChild>
        <AppLink href={consoleRoutes.evaluations(summary.slug, agentId)}>
          <Icon name="flask" />
          {t("build.agentSettings.retrieval.runEval")}
        </AppLink>
      </Button>
    </div>
  );
}

export function RetrievalTab({ agentId, sourceCount, disabled, graphAvailable = false }: { agentId: string; sourceCount: number; disabled: boolean; graphAvailable?: boolean }) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const form = useFormContext<SettingsValues>();
  const rerank = useWatch({ control: form.control, name: "retrieval.rerank_enabled" });
  const [advanced, setAdvanced] = useState(false);
  const pct = (v: number) => `${String(Math.round(v * PERCENT))}%`;
  return (
    <>
      <div className="flex items-start gap-3 rounded-lg border border-line bg-info-soft px-4 py-3">
        <Icon name="info" className="mt-0.5 size-4 shrink-0 text-info" />
        <p className="text-sm"><strong>{t("build.agentSettings.retrieval.info")}</strong> <span className="text-fg-muted">{t("build.agentSettings.retrieval.infoBody")}</span></p>
      </div>
      <SettingsCard title={t("build.agentSettings.retrieval.chunking")} hint={t("build.agentSettings.retrieval.chunkingHint", { count: sourceCount })}>
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <Field label={t("build.agentSettings.retrieval.chunkSize")}>
            <Select disabled={disabled} {...form.register("retrieval.chunk_target_tokens", { valueAsNumber: true })}>
              {CHUNK_SIZES.map((size) => <option key={size} value={size}>{t(`build.agentSettings.retrieval.chunkSizes.${String(size) as "256" | "512" | "1024"}`)}</option>)}
            </Select>
          </Field>
          <Range name="retrieval.chunk_overlap_ratio" label={t("build.agentSettings.retrieval.overlap")} min={0} max={LIMITS.overlapMax} step={OVERLAP_STEP} format={pct} hint={t("build.agentSettings.retrieval.overlapHint")} disabled={disabled} />
        </div>
      </SettingsCard>
      <SettingsCard title={t("build.agentSettings.retrieval.search")} hint={t("build.agentSettings.retrieval.searchHint")}>
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <Range name="retrieval.k_fused" label={t("build.agentSettings.retrieval.topK")} min={LIMITS.kMin} max={LIMITS.kMax} step={K_STEP} hint={t("build.agentSettings.retrieval.topKHint")} disabled={disabled} />
          <Range name="retrieval.final_top_n" label={t("build.agentSettings.retrieval.final")} min={LIMITS.finalMin} max={LIMITS.finalMax} step={1} hint={t("build.agentSettings.retrieval.finalHint")} disabled={disabled} />
        </div>
        <Range
          name="retrieval.hybrid"
          label={t("build.agentSettings.retrieval.hybrid")}
          min={0}
          max={PERCENT}
          step={HYBRID_STEP}
          format={(v) => t("build.agentSettings.retrieval.hybridValue", { keyword: PERCENT - v, meaning: v })}
          startLabel={t("build.agentSettings.retrieval.keyword")}
          endLabel={t("build.agentSettings.retrieval.meaning")}
          disabled={disabled}
        />
        <div className="divider" />
        <SwitchRow name="retrieval.rerank_enabled" label={t("build.agentSettings.retrieval.rerank")} hint={t("build.agentSettings.retrieval.rerankHint")} disabled={disabled} className="" />
        {rerank ? (
          <div className="grid grid-cols-1 gap-5 rounded-md bg-surface-2 p-4 md:grid-cols-2">
            <p className="text-xs text-fg-muted md:self-center">{t("build.agentSettings.retrieval.rerankModel")}</p>
            <Range name="retrieval.rerank_min_score" label={t("build.agentSettings.retrieval.threshold")} min={0} max={LIMITS.rerankMax} step={SCORE_STEP} format={(v) => v.toFixed(2)} hint={t("build.agentSettings.retrieval.thresholdHint")} disabled={disabled} />
          </div>
        ) : null}
        <div>
          <Button type="button" variant="ghost" size="sm" aria-expanded={advanced} aria-controls="retrieval-advanced" onClick={() => { setAdvanced((v) => !v); }}>
            <Icon name={advanced ? "chevron-up" : "chevron-down"} />
            {t("build.agentSettings.retrieval.advanced")}
          </Button>
          {advanced ? (
            <div id="retrieval-advanced" className="mt-3 grid grid-cols-1 gap-5 rounded-md border border-line p-4 md:grid-cols-2">
              <Range name="retrieval.k_dense" label={t("build.agentSettings.retrieval.kDense")} min={LIMITS.kMin} max={LIMITS.kMax} step={K_STEP} disabled={disabled} />
              <Range name="retrieval.k_lexical" label={t("build.agentSettings.retrieval.kLexical")} min={LIMITS.kMin} max={LIMITS.kMax} step={K_STEP} disabled={disabled} />
              <Range name="retrieval.max_chunks_per_source" label={t("build.agentSettings.retrieval.perSource")} min={1} max={LIMITS.perSourceMax} step={1} hint={t("build.agentSettings.retrieval.perSourceHint")} disabled={disabled} />
              <SwitchRow name="retrieval.neighbor_expansion" label={t("build.agentSettings.retrieval.neighbors")} hint={t("build.agentSettings.retrieval.neighborsHint")} disabled={disabled} className="" />
              {graphAvailable ? (
                <SwitchRow
                  name="retrieval.graph_retrieval"
                  label={<>{t("build.agentSettings.retrieval.graph")} <Badge tone="brand" className="ml-1">{t("build.agentSettings.retrieval.graphStage")}</Badge></>}
                  hint={t("build.agentSettings.retrieval.graphHint")}
                  disabled={disabled}
                  className=""
                />
              ) : null}
            </div>
          ) : null}
        </div>
      </SettingsCard>
      <SettingsCard title={t("build.agentSettings.retrieval.understanding")} flush>
        <div className="divide-y divide-line">
          <SwitchRow name="retrieval.query_rewrite" label={t("build.agentSettings.retrieval.rewrite")} hint={t("build.agentSettings.retrieval.rewriteHint")} disabled={disabled} />
          <SwitchRow name="retrieval.contextual_retrieval" label={<>{t("build.agentSettings.retrieval.contextual")} <Badge tone="brand" className="ml-1">{t("build.agentSettings.retrieval.reindex")}</Badge></>} hint={t("build.agentSettings.retrieval.contextualHint")} disabled={disabled} />
          <SwitchRow name="retrieval.multi_query" label={t("build.agentSettings.retrieval.multiQuery")} hint={t("build.agentSettings.retrieval.multiQueryHint")} disabled={disabled} />
          <div className="flex items-start gap-3 px-5 py-4">
            <Icon name="filter" className="mt-0.5 size-4 shrink-0 text-fg-subtle" />
            <p className="text-xs text-fg-muted">
              <span className="text-sm font-semibold text-fg">{t("build.agentSettings.retrieval.filters")}</span> {t("build.agentSettings.retrieval.filtersBody")}{" "}
              <AppLink href={consoleRoutes.knowledge(summary.slug, agentId)} className="font-semibold text-primary-ink hover:underline">{t("build.agentSettings.retrieval.knowledgeLink")}</AppLink>
            </p>
          </div>
        </div>
      </SettingsCard>
      <LastEvaluation agentId={agentId} />
    </>
  );
}
